import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { normalizarValorOrdenacao } from '../../../shared/helpers/ordenacao-tabela.helper';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';

import { HeaderComponent } from '../../../shared/components/header/header.component';
import { UiTagComponent } from '../../../shared/components/ui-tag/ui-tag.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxDefaultListComponent } from '../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { ConfiguracaoColunasModalComponent, ColunaConfiguracao } from '../../../shared/components/configuracao-colunas-modal/configuracao-colunas-modal.component';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { MovimentacaoService } from '../../../shared/services/apis/financas/movimentacao.service';
import { FechamentoFinanceiroService } from '../../../shared/services/apis/financas/fechamento-financeiro.service';
import { TransferenciaService } from '../../../shared/services/apis/financas/transferencia.service';
import { PessoaLookupService } from '../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { ListPessoaIdsRequest } from '../../../shared/services/requests/pessoa/list-pessoa-ids-request';
import { MaskService } from '../../../shared/services/mask.service';
import { NaturezaMovimentacaoEnum } from '../../../shared/enums/financas/natureza-movimentacao.enum';

import { ModalTagsComponent, ModalTagsResult } from './dialogs/modal-tags/modal-tags.component';
import { ModalAlterarCategoriaComponent, ModalAlterarCategoriaResult } from './dialogs/modal-alterar-categoria/modal-alterar-categoria.component';
import { ModalAlterarContaComponent, ModalAlterarContaResult } from './dialogs/modal-alterar-conta/modal-alterar-conta.component';
import { ModalFechamentoFinanceiroComponent } from './dialogs/modal-fechamento-financeiro/modal-fechamento-financeiro.component';
import { FiltroItem, ModalFiltrosComponent, ModalFiltrosResult } from './dialogs/modal-filtros/modal-filtros.component';
import { ModalImportarLancamentosComponent } from './dialogs/modal-importar-lancamentos/modal-importar-lancamentos.component';
import { ImpressaoExtratoMovimentacoesComponent } from './dialogs/impressao-extrato-movimentacoes/impressao-extrato-movimentacoes.component';
import { ImpressaoRecibosComponent } from './dialogs/impressao-recibos/impressao-recibos.component';
import { AbntPipe } from '../../../shared/pipes/abnt.pipe';
import { TruncatePipe } from '../../../shared/pipes/truncate.pipe';
import { PrintMovimentacaoRequest } from '../../../shared/services/requests/financas/print-movimentacao.request';
import { MovimentacaoGridItem } from '../../../shared/services/responses/financas/get-movimentacao.response';
import { MovimentacaoSaldoContaAtual } from '../../../shared/services/responses/financas/print-movimentacao.response';
import { ExportacaoLancamentoPreviewRow, ModalExportarLancamentosComponent } from './dialogs/modal-exportar-lancamentos/modal-exportar-lancamentos.component';
import { UtilService } from '../../../shared/services/util.service';


export interface LancamentoFinanceiro {
    id: number;
    idContaFinanceira: number;
    idNaturezaMovimentacao: number;
    data: Date;
    historico: string;
    idPessoa?: number;
    cliente?: string;
    categoria?: string;
    valor: number;
    tipo: 'entrada' | 'saida';
    tags?: { id: number, descricao: string, cor: string }[];
    origem: string;
    situacao: 'conciliado' | 'naoConciliado';
    nrDocumento?: string;
    isTransferencia: boolean;
    idTransferencias?: number;
    /** Pedido de venda que gerou a movimentação: editar e excluir só pelo pedido. */
    idVenda?: number;
    /** NF-e de saída que gerou a movimentação: editar e excluir só pela nota. */
    idNFeSaida?: number;
}

export interface ContaFinanceira {
    id: number;
    nome: string;
    saldo: number;
    sigla: string;
    cor: string;
}

interface FiltrosAvancadosExtrato {
    idsCategorias: number[];
    idsTags: number[];
    situacoes: string[];
}

@Component({
    selector: 'app-extrato-movimentacoes',
    standalone: true,
    imports: [
        CommonModule,
        HeaderComponent,
        ReactiveFormsModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatBadgeModule,
        MatIconModule,
        MatTableModule,
        MatSortModule,
        MatCheckboxModule,
        MatMenuModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        MatSelectModule,
        EmptyListComponent,
        LoadingBoxDefaultListComponent,
        RouterModule,
        MatDividerModule,
        MatDatepickerModule,
        AbntPipe,
        TruncatePipe,
        UiTagComponent,
    ],
    providers: [AbntPipe],
    templateUrl: './extrato-movimentacoes.component.html',
    styleUrls: ['./extrato-movimentacoes.component.css'],
})
export class ExtratoMovimentacoesComponent implements OnInit, OnDestroy {
    public readonly PeriodoExtratoEnum = {
        Hoje: 'hoje',
        DaSemana: 'semana',
        DoMes: 'mes',
        Ultimos30Dias: 'ultimos30dias',
        DoIntervalo: 'periodo',
    } as const;

    public moduloEnum = ModuloEnum.Financas;
    public funcionalidadeEnum = FuncionalidadeEnum.Financas_Extrato;

    public model: FormGroup;
    public _isLoading: boolean = false;
    public _isLoadingAction: boolean = false;
    public _ocultarValores: boolean = false;

    public _dataSource = new MatTableDataSource<LancamentoFinanceiro>();
    public _paginaAtual: number = 1;
    public _tamanhoPagina: number = 20;
    public _ultimaPagina: boolean = false;
    public _totalRegistros: number = 0;

    public selection = new SelectionModel<LancamentoFinanceiro>(true, []);

    @ViewChild(MatSort) set matSort(sort: MatSort) {
        this._dataSource.sort = sort;
        this._dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
                case 'data':
                    return normalizarValorOrdenacao(item.data?.getTime?.() ?? 0);
                case 'tags':
                    return normalizarValorOrdenacao(item.tags?.map(tag => tag.descricao).join(', ') ?? '');
                case 'situacao':
                    return normalizarValorOrdenacao(this.getSituacaoLabel(item.situacao));
                case 'valor':
                    // "valor" é a magnitude; o sinal vem do tipo. Ordena pelo valor
                    // com sinal para que saídas (negativas) fiquem abaixo das entradas.
                    return item.tipo === 'saida' ? -(item.valor ?? 0) : (item.valor ?? 0);
                default:
                    return normalizarValorOrdenacao((item as any)[property] ?? '');
            }
        };
    }

    // Período fechamento financeiro
    public _dataFechamento: Date | null = null;

    // Totais rodapé
    public _saldoInicial: number = 0;
    public _totalEntradas: number = 0;
    public _totalSaidas: number = 0;
    public get _saldoFinal(): number {
        return this._saldoInicial + this._totalEntradas - this._totalSaidas;
    }

    // Contas financeiras (carregadas do servidor)
    public _contas: ContaFinanceira[] = [];
    public _saldosContasAtuais: MovimentacaoSaldoContaAtual[] = [];
    public _categoriasFiltro: FiltroItem[] = [];
    public _tagsFiltro: FiltroItem[] = [];
    public _situacoesFiltro: string[] = ['conciliado', 'naoConciliado'];
    public get _filtrosAvancados(): FiltrosAvancadosExtrato {
        return {
            idsCategorias: this.model?.get('idsCategorias')?.value ?? [],
            idsTags: this.model?.get('idsTags')?.value ?? [],
            situacoes: this.model?.get('situacoes')?.value ?? [],
        };
    }

    private _colors = ['#e53935', '#ff9800', '#43a047', '#1e88e5', '#8e24aa', '#00897b', '#f4511e'];
    private _lancamentosBase: LancamentoFinanceiro[] = [];
    private _clientesPorId = new Map<number, string>();

    // Colunas configuráveis (padrão do projeto)
    public _colunasVisiveis: string[] = [];
    public todosCampos: ColunaConfiguracao[] = [
        { def: 'select', label: 'Seleção', visible: true, fixed: true, pinnedStart: true },
        { def: 'data', label: 'Data', visible: true, fixed: false },
        { def: 'historico', label: 'Histórico', visible: true, fixed: false },
        { def: 'cliente', label: 'Cliente', visible: false, fixed: false },
        { def: 'categoria', label: 'Categoria', visible: true, fixed: false },
        { def: 'valor', label: 'Valor (R$)', visible: true, fixed: false },
        { def: 'tags', label: 'Tags', visible: true, fixed: false },
        { def: 'origem', label: 'Origem', visible: true, fixed: false },
        { def: 'situacao', label: 'Situação', visible: true, fixed: false },
        { def: 'actions', label: 'Ações', visible: true, fixed: true, pinnedEnd: true },
    ];

    private _subscriptions = new Subscription();
    private abntPipe: AbntPipe;

    constructor(
        private formBuilder: FormBuilder,
        private alertService: AlertService,
        private dialog: MatDialog,
        private movimentacaoService: MovimentacaoService,
        private transferenciaService: TransferenciaService,
        private fechamentoFinanceiroService: FechamentoFinanceiroService,
        private pessoaLookupService: PessoaLookupService,
        private utilService: UtilService,
        abntPipe: AbntPipe,
    ) {
        this.abntPipe = abntPipe;
    }

    ngOnInit(): void {
        this.model = this.formBuilder.group({
            pesquisa: [''],
            conta: ['todas'],
            periodo: ['ultimos30dias'],
            dataInicioCustom: [null],
            dataFimCustom: [null],
            idsCategorias: [[] as number[]],
            idsTags: [[] as number[]],
            situacoes: [[] as string[]],
        });
        this.loadFilters();
        this.carregarContas();
        this.carregarLancamentos(true);
        this.carregarFechamento();
        this.atualizarColunasVisiveis();
    }

    private loadFilters(): void {
        const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
        if (filtroSalvo) {
            this.model.patchValue(filtroSalvo);
        }
    }

    private atualizarColunasVisiveis(): void {
        this._colunasVisiveis = this.todosCampos
            .filter(c => c.visible)
            .map(c => c.def);
    }

    private carregarFechamento(): void {
        this._subscriptions.add(
            this.fechamentoFinanceiroService.get().subscribe({
                next: (response) => {
                    if (!response.isError && response.fechamentoFinanceiro) {
                        this._dataFechamento = new Date(response.fechamentoFinanceiro.dataFechamento);
                    }
                }
            })
        );
    }

    ngOnDestroy(): void {
        this._subscriptions.unsubscribe();
        this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, ['/financas/lancamentos/editar', '/financas/lancamentos/cadastrar']);
    }

    private carregarContas(): void {
        this._subscriptions.add(
            this.movimentacaoService.getLookup().subscribe({
                next: response => {
                    if (!response.isError) {
                        this._contas = (response.listaContaFinanceira || []).map((c, i) => ({
                            id: c.idContaFinanceira ?? 0,
                            nome: c.descricao,
                            saldo: 0,
                            sigla: c.descricao.charAt(0).toUpperCase(),
                            cor: this._colors[i % this._colors.length]
                        }));
                        this._categoriasFiltro = (response.listaCategoriasFinanceira || [])
                            .filter(c => c.isAtivo)
                            .map(c => ({ id: c.idCategoriaFinanceira, descricao: c.descricao }))
                            .sort((a, b) => a.descricao.localeCompare(b.descricao));
                        this._tagsFiltro = (response.listaTagsMovimentacao || [])
                            .map(t => ({ id: t.idTagMovimentacao, descricao: t.descricao }))
                            .sort((a, b) => a.descricao.localeCompare(b.descricao));
                    }
                }
            })
        );
    }

    private buildDateRange(): { dataInicio?: string; dataFim?: string } {
        const periodo = this.model?.get('periodo')?.value;
        const now = new Date();
        const toIso = (d: Date) => d.toISOString();
        const startOfDay = (d: Date) => { d.setHours(0, 0, 0, 0); return d; };
        const endOfDay = (d: Date) => { d.setHours(23, 59, 59, 999); return d; };

        switch (periodo) {
            case 'hoje':
                return { dataInicio: toIso(startOfDay(new Date(now))), dataFim: toIso(endOfDay(new Date(now))) };
            case 'semana': {
                const inicio = new Date(now);
                inicio.setDate(now.getDate() - 6);
                return { dataInicio: toIso(startOfDay(inicio)), dataFim: toIso(endOfDay(new Date(now))) };
            }
            case 'mes': {
                const inicio = new Date(now.getFullYear(), now.getMonth(), 1);
                return { dataInicio: toIso(startOfDay(inicio)), dataFim: toIso(endOfDay(new Date(now))) };
            }
            case 'ultimos30dias': {
                const inicio = new Date(now);
                inicio.setDate(now.getDate() - 29);
                return { dataInicio: toIso(startOfDay(inicio)), dataFim: toIso(endOfDay(new Date(now))) };
            }
            case 'periodo': {
                const raw = this.model?.getRawValue();
                const di = raw?.dataInicioCustom ? new Date(raw.dataInicioCustom) : null;
                const df = raw?.dataFimCustom ? new Date(raw.dataFimCustom) : null;
                return {
                    dataInicio: di ? toIso(startOfDay(di)) : undefined,
                    dataFim: df ? toIso(endOfDay(df)) : undefined,
                };
            }
            case 'semFiltro':
            default:
                return {};
        }
    }

    private carregarLancamentos(reset: boolean = false): void {
        if (this._isLoading) return;
        this._isLoading = true;

        const raw = this.model?.getRawValue();
        const { dataInicio, dataFim } = this.buildDateRange();
        const idContaFinanceira = raw?.conta && raw.conta !== 'todas' ? Number(raw.conta) : undefined;
        const idsSituacaoMovimentacao = this.mapSituacoesFiltroParaIds();

        this._subscriptions.add(
            this.movimentacaoService.getAll({
                pesquisa: raw?.pesquisa || undefined,
                dataInicio,
                dataFim,
                idContaFinanceira,
                idsCategoriaFinanceira: this._filtrosAvancados.idsCategorias.length ? this._filtrosAvancados.idsCategorias : undefined,
                idsTagsMovimentacao: this._filtrosAvancados.idsTags.length ? this._filtrosAvancados.idsTags : undefined,
                idsSituacaoMovimentacao: idsSituacaoMovimentacao.length ? idsSituacaoMovimentacao : undefined,
                pagina: this._paginaAtual,
                tamanhoPagina: this._tamanhoPagina,
            }).subscribe({
                next: response => {
                    this._isLoading = false;
                    if (response.isError) {
                        this.alertService.show('Erro', response.errorDescription || 'Erro ao carregar lançamentos.', AlertType.error);
                        return;
                    }
                    const itens: LancamentoFinanceiro[] = (response.itens || []).map(item => ({
                        id: item.idMovimentacao,
                        idContaFinanceira: item.idContaFinanceira,
                        idNaturezaMovimentacao: item.idNaturezaMovimentacao,
                        data: new Date(item.dataMovimentacao),
                        historico: this.stripHtml(item.descricao || ''),
                        idPessoa: item.idPessoa ?? undefined,
                        cliente: item.idPessoa ? this._clientesPorId.get(item.idPessoa) : undefined,
                        categoria: item.descricaoCategoria || undefined,
                        valor: item.valor,
                        tipo: item.isEntrada ? 'entrada' : 'saida',
                        tags: item.tags.map((m: any) => ({ id: m.id, descricao: m.descricao, cor: m.cor })),
                        origem: item.descricaoContaFinanceira,
                        situacao: this.mapSituacaoMovimentacao(item.idSituacaoMovimentacao, item.descricaoSituacaoMovimentacao),
                        isTransferencia: item.isTransferencia ?? false,
                        idTransferencias: item.idTransferencias ?? undefined,
                        idVenda: item.idVenda ?? undefined,
                        idNFeSaida: item.idNFeSaida ?? undefined,
                    }));
                    this._lancamentosBase = reset ? itens : [...this._lancamentosBase, ...itens];
                    this._ultimaPagina = this._lancamentosBase.length >= response.quantidadeLancamentos;
                    if (reset) {
                        this._saldoInicial = response.saldoInicial;
                        this._totalEntradas = response.totalEntradas;
                        this._totalSaidas = response.totalSaidas;
                        this._totalRegistros = response.quantidadeLancamentos;
                    }
                    if (this.isColunaClienteVisivel) {
                        this.carregarClientesDosLancamentos();
                    } else {
                        this.aplicarFiltrosLocais();
                    }
                },
                error: () => {
                    this._isLoading = false;
                    this.alertService.show('Erro', 'Não foi possível carregar os lançamentos.', AlertType.error);
                }
            })
        );

        if (reset) {
            this.carregarSaldosContasAtuais();
        }
    }

    private carregarSaldosContasAtuais(): void {
        this._subscriptions.add(
            this.movimentacaoService.getPrint(this.getFiltrosPrintRequest()).subscribe({
                next: (response) => {
                    if (response.isError) {
                        this._saldosContasAtuais = [];
                        return;
                    }

                    this._saldosContasAtuais = response.saldosContasAtuais || [];
                },
                error: () => {
                    this._saldosContasAtuais = [];
                }
            })
        );
    }

    public clickPesquisar(): void {
        this._paginaAtual = 1;
        this._dataSource.data = [];
        this._ultimaPagina = false;
        this.selection.clear();
        this.carregarLancamentos(true);
    }

    public limparFiltros(): void {
        this.model.reset({
            pesquisa: '',
            conta: 'todas',
            periodo: 'ultimos30dias',
            dataInicioCustom: null,
            dataFimCustom: null,
            idsCategorias: [],
            idsTags: [],
            situacoes: [],
        });
        this.clickPesquisar();
    }

    public proximaPagina(): void {
        if (this._ultimaPagina || this._isLoading) return;
        this._paginaAtual++;
        this.carregarLancamentos(false);
    }

    public toggleOcultarValores(): void {
        this._ocultarValores = !this._ocultarValores;
    }

    public isMobileDrawerOpen: boolean = false;

    public toggleGavetaMobile(): void {
        this.isMobileDrawerOpen = !this.isMobileDrawerOpen;
        document.body.style.overflow = this.isMobileDrawerOpen ? 'hidden' : '';
    }

    public fecharGavetaMobile(): void {
        if (this.isMobileDrawerOpen) {
            this.isMobileDrawerOpen = false;
            document.body.style.overflow = '';
        }
    }

    public get periodoSelecionado(): string {
        return this.model?.get('periodo')?.value;
    }

    public get periodoLabel(): string {
        switch (this.periodoSelecionado) {
            case this.PeriodoExtratoEnum.Hoje:
                return 'hoje';
            case this.PeriodoExtratoEnum.DaSemana:
                return 'desta semana';
            case this.PeriodoExtratoEnum.DoMes:
                return 'do mês';
            case this.PeriodoExtratoEnum.DoIntervalo:
                return 'do intervalo';
            case this.PeriodoExtratoEnum.Ultimos30Dias:
            default:
                return 'últimos 30 dias';
        }
    }

    public prepararPeriodoIntervalo(): void {
        // Mantido para seguir o mesmo padrão das telas de contas.
    }

    public selecionarPeriodo(periodo: string): void {
        this.model.get('periodo')?.setValue(periodo);
    }

    public aplicarPeriodo(menuTrigger?: MatMenuTrigger): void {
        if (this.periodoSelecionado === this.PeriodoExtratoEnum.DoIntervalo) {
            const dataInicio = this.model.get('dataInicioCustom')?.value;
            const dataFim = this.model.get('dataFimCustom')?.value;
            if (!dataInicio || !dataFim) {
                return;
            }

            if (new Date(dataInicio) > new Date(dataFim)) {
                this.alertService.show('Atenção', 'A data final não pode ser menor que a data inicial.', AlertType.warning);
                return;
            }
        }

        menuTrigger?.closeMenu();
        this.clickPesquisar();
    }

    public cancelarPeriodoIntervalo(menuTrigger: MatMenuTrigger): void {
        this.model.patchValue({
            periodo: this.PeriodoExtratoEnum.Ultimos30Dias,
            dataInicioCustom: null,
            dataFimCustom: null,
        });
        menuTrigger.closeMenu();
    }

    public onDateInput(event: Event, controlName: 'dataInicioCustom' | 'dataFimCustom'): void {
        MaskService.onDateInput(event, this.model.get(controlName));
    }

    public onPeriodoMenuClosed(): void {
        if (this.periodoSelecionado !== this.PeriodoExtratoEnum.DoIntervalo) {
            this.model.patchValue({
                dataInicioCustom: null,
                dataFimCustom: null,
            }, { emitEvent: false });
        }
    }

    // Selection
    public isAllSelected(): boolean {
        return this.selection.selected.length === this._dataSource.data.length && this._dataSource.data.length > 0;
    }

    public toggleAllRows(): void {
        if (this.isAllSelected()) {
            this.selection.clear();
        } else {
            this.selection.select(...this._dataSource.data);
        }
    }

    public checkboxLabel(row?: LancamentoFinanceiro): string {
        if (!row) return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id}`;
    }

    // Actions
    public abrirModalTags(lancamento?: LancamentoFinanceiro): void {
        const selecionados = lancamento ? [lancamento] : this.selection.selected;
        if (selecionados.length === 0) return;
        const dialogRef = this.dialog.open(ModalTagsComponent, {
            width: '520px',
            data: { lancamentos: selecionados }
        });
        dialogRef.afterClosed().subscribe((result: ModalTagsResult | undefined) => {
            if (!result?.success) return;
            this.selection.clear();
            this.clickPesquisar();
        });
    }

    public abrirModalAlterarCategoria(): void {
        if (this.selection.selected.length === 0) return;
        const dialogRef = this.dialog.open(ModalAlterarCategoriaComponent, {
            width: '480px',
            data: { lancamentos: this.selection.selected }
        });
        dialogRef.afterClosed().subscribe((result: ModalAlterarCategoriaResult | undefined) => {
            if (!result) return;
            const ids = this.selection.selected.map(l => l.id);
            this.movimentacaoService.bulkUpdateCategoria(ids, result.idCategoriaFinanceira).subscribe({
                next: (response) => {
                    if (response.isError) {
                        this.alertService.show('Erro', response.errorDescription || 'Erro ao alterar categoria.', AlertType.error);
                        return;
                    }
                    this.alertService.show('Sucesso', 'Categoria alterada com sucesso.', AlertType.success);
                    this.selection.clear();
                    this.clickPesquisar();
                },
                error: () => {
                    this.alertService.show('Erro', 'Erro ao alterar categoria.', AlertType.error);
                }
            });
        });
    }

    public abrirModalAlterarConta(): void {
        if (this.selection.selected.length === 0) return;

        const idsContaOrigem = [...new Set(
            this.selection.selected
                .map(lancamento => lancamento.idContaFinanceira)
                .filter(idContaFinanceira => idContaFinanceira > 0)
        )];

        if (idsContaOrigem.length > 1) {
            this.alertService.show(
                'Atenção',
                'Não é possível alterar a conta financeira de lançamentos com contas diferentes.',
                AlertType.warning
            );
            return;
        }

        const dialogRef = this.dialog.open(ModalAlterarContaComponent, {
            width: '480px',
            data: { lancamentos: this.selection.selected }
        });
        dialogRef.afterClosed().subscribe((result: ModalAlterarContaResult | undefined) => {
            if (!result) return;
            const ids = this.selection.selected.map(l => l.id);
            this.movimentacaoService.bulkUpdateConta(ids, result.idContaFinanceira).subscribe({
                next: (response) => {
                    if (response.isError) {
                        this.alertService.show('Erro', response.errorDescription || 'Erro ao alterar conta financeira.', AlertType.error);
                        return;
                    }
                    this.alertService.show('Sucesso', 'Conta financeira alterada com sucesso.', AlertType.success);
                    this.selection.clear();
                    this.clickPesquisar();
                },
                error: () => {
                    this.alertService.show('Erro', 'Erro ao alterar conta financeira.', AlertType.error);
                }
            });
        });
    }

    public abrirModalFechamento(): void {
        const dialogRef = this.dialog.open(ModalFechamentoFinanceiroComponent, {
            width: '560px',
            data: { dataFechamento: this._dataFechamento }
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (result == null || typeof result !== 'object') return;
            this._dataFechamento = result.dataFechamento ?? null;
            if (this._dataFechamento) {
                this.alertService.show('Sucesso', 'Período financeiro fechado com sucesso.', AlertType.success);
            } else {
                this.alertService.show('Sucesso', 'Data de fechamento removida com sucesso.', AlertType.success);
            }
        });
    }

    public abrirModalConfigurarColunas(): void {
        const dialogRef = this.dialog.open(ConfiguracaoColunasModalComponent, {
            maxWidth: '700px',
            data: { colunas: this.todosCampos }
        });

        dialogRef.afterClosed().subscribe((result: ColunaConfiguracao[] | undefined) => {
            if (result) {
                this.todosCampos = result;
                this.atualizarColunasVisiveis();

                if (this.isColunaClienteVisivel) {
                    this.carregarClientesDosLancamentos();
                } else {
                    this.aplicarFiltrosLocais();
                }
            }
        });
    }

    public abrirModalFiltros(): void {
        this.dialog.open(ModalFiltrosComponent, {
            width: '520px',
            data: {
                categorias: this._categoriasFiltro,
                tags: this._tagsFiltro,
                situacoes: this._situacoesFiltro,
                selecionados: {
                    idsCategorias: [...this._filtrosAvancados.idsCategorias],
                    idsTags: [...this._filtrosAvancados.idsTags],
                    situacoes: [...this._filtrosAvancados.situacoes],
                }
            }
        }).afterClosed().subscribe((result: ModalFiltrosResult | undefined) => {
            if (result) {
                this.model.patchValue({
                    idsCategorias: result.idsCategorias,
                    idsTags: result.idsTags,
                    situacoes: result.situacoes,
                });
                this.clickPesquisar();
            }
        });
    }

    public abrirModalImportarLancamentos(): void {
        const dialogRef = this.dialog.open(ModalImportarLancamentosComponent, {
            width: '600px',
            disableClose: true,
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (result) this.clickPesquisar();
        });
    }

    public exportarLancamentos(): void {
        if (this._isLoadingAction) return;

        this._isLoadingAction = true;
        const filtros = this.getFiltrosPrintRequest();

        this._subscriptions.add(
            this.movimentacaoService.getPrint(filtros).subscribe({
                next: (response) => {
                    if (response.isError) {
                        this._isLoadingAction = false;
                        this.alertService.show('Erro', response.errorDescription || 'Não foi possível exportar os lançamentos.', AlertType.error);
                        return;
                    }

                    this._isLoadingAction = false;
                    this.exportarLancamentosCsv(response.itens || []);
                },
                error: () => {
                    this._isLoadingAction = false;
                    this.alertService.show('Erro', 'Não foi possível exportar os lançamentos.', AlertType.error);
                }
            })
        );
    }

    public imprimirRelatorio(): void {
        this.dialog.open(ImpressaoExtratoMovimentacoesComponent, {
            data: {
                filtros: this.getFiltrosPrintRequest(),
                resumoFiltros: this.getResumoFiltrosAplicados()
            },
            maxWidth: '90vw',
            width: '90vw',
            maxHeight: '90vh'
        });
    }

    private getResumoFiltrosAplicados(): string[] {
        const raw = this.model?.getRawValue();
        const resumo: string[] = [];

        const idConta = raw?.conta && raw.conta !== 'todas' ? Number(raw.conta) : undefined;
        resumo.push(`Conta: ${idConta ? this._contas.find(c => c.id === idConta)?.nome ?? '-' : 'Todas'}`);

        resumo.push(`Período: ${this.periodoLabel}`);

        if (this._filtrosAvancados.idsCategorias.length) {
            const nomes = this._filtrosAvancados.idsCategorias
                .map(id => this._categoriasFiltro.find(c => c.id === id)?.descricao)
                .filter(Boolean);
            resumo.push(`Categoria: ${nomes.join(', ')}`);
        }

        if (this._filtrosAvancados.situacoes.length) {
            const nomes = this._filtrosAvancados.situacoes.map(s => this.getSituacaoLabel(s));
            resumo.push(`Situação: ${nomes.join(', ')}`);
        }

        return resumo;
    }

    public imprimirRecibos(lancamento?: LancamentoFinanceiro): void {
        const lancamentos = lancamento ? [lancamento] : this.selection.selected;
        if (lancamentos.length === 0) return;
        this.dialog.open(ImpressaoRecibosComponent, {
            data: { lancamentos },
            maxWidth: '90vw',
            width: '720px',
            maxHeight: '90vh'
        });
    }

    public excluirSelecionados(): void {
        if (this.selection.selected.length === 0) return;

        const temTransferencia = this.selection.selected.some(l => l.isTransferencia);
        if (temTransferencia) {
            this.alertService.show(
                'Ação não permitida',
                'Lançamentos de transferência não podem ser excluídos em lote. Exclua-os individualmente pelo menu de ações.',
                AlertType.error
            );
            return;
        }

        const ids = this.selection.selected.map(l => l.id);
        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Confirmar exclusão',
                description: `Confirma a exclusão de ${ids.length} lançamento(s)?`,
                buttonConfirm: 'Confirmar',
                buttonClose: 'Cancelar'
            }
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (result?.confirm) {
                this._subscriptions.add(
                    this.movimentacaoService.bulkDelete(ids).subscribe({
                        next: (response) => {
                            if (response.isError) {
                                this.alertService.show('Erro', response.errorDescription || 'Erro ao excluir lançamentos.', AlertType.error);
                                return;
                            }
                            this.alertService.show('Sucesso', `${ids.length} lançamento(s) excluído(s) com sucesso.`, AlertType.success);
                            this.selection.clear();
                            this.clickPesquisar();
                        },
                        error: () => {
                            this.alertService.show('Erro', 'Não foi possível excluir os lançamentos.', AlertType.error);
                        }
                    })
                );
            }
        });
    }

    public excluirLancamento(lancamento: LancamentoFinanceiro): void {
        const isTransferencia = lancamento.isTransferencia && !!lancamento.idTransferencias;
        const descricao = isTransferencia
            ? `Confirma a exclusão da transferência "${lancamento.historico}"? Ambos os lançamentos (débito e crédito) serão removidos.`
            : `Confirma a exclusão do lançamento "${lancamento.historico}"? Esta ação não pode ser desfeita.`;

        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: { title: 'Confirmar exclusão', description: descricao, buttonConfirm: 'Excluir', buttonClose: 'Cancelar' }
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result?.confirm) {
                const delete$ = isTransferencia
                    ? this.transferenciaService.delete(lancamento.idTransferencias!)
                    : this.movimentacaoService.delete(lancamento.id);

                this._subscriptions.add(
                    delete$.subscribe({
                        next: (response) => {
                            if (response.isError) {
                                this.alertService.show('Erro', response.errorDescription || 'Não foi possível excluir.', AlertType.error);
                                return;
                            }
                            this.alertService.show('Sucesso', isTransferencia ? 'Transferência excluída com sucesso.' : 'Lançamento excluído com sucesso.', AlertType.success);
                            this.selection.clear();
                            this.clickPesquisar();
                        },
                        error: () => {
                            this.alertService.show('Erro', 'Não foi possível excluir.', AlertType.error);
                        }
                    })
                );
            }
        });
    }

    // Formatters
    public formatarValor(valor: number, idNaturezaMovimentacao: number): string {
        const formatted = this.abntPipe.transform(valor, 'preco', 2);
        return idNaturezaMovimentacao === NaturezaMovimentacaoEnum.Entrada ? `+ ${formatted}` : `- ${formatted}`;
    }

    public getSituacaoClass(situacao: string): string {
        switch (situacao) {
            case 'conciliado': return 'badge-verde';
            case 'naoConciliado': return 'badge-amarelo';
            default: return '';
        }
    }

    public getSituacaoLabel(situacao: string): string {
        switch (situacao) {
            case 'conciliado': return 'Conciliado';
            case 'naoConciliado': return 'Não Conciliado';
            default: return situacao;
        }
    }

    public getSiglaOrigem(origem: string): string {
        return origem.charAt(0).toUpperCase();
    }

    public getCorOrigem(origem: string): string {
        const conta = this._contas.find(c => c.nome === origem);
        return conta?.cor || '#9e9e9e';
    }

    public getCorContaSaldo(idContaFinanceira: number, descricaoContaFinanceira: string): string {
        const conta = this._contas.find(c => c.id === idContaFinanceira || c.nome === descricaoContaFinanceira);
        return conta?.cor || '#9e9e9e';
    }

    /** Lançamento gerado por documento (pedido de venda ou NF-e de saída) na seleção: conta e exclusão em lote só pelo documento. */
    public get temSelecionadoDaVenda(): boolean {
        return this.selection.selected.some(l => this.isOrigemDocumento(l));
    }

    /** Motivo das ações em lote bloqueadas pela seleção com lançamento gerado por documento. */
    public get tooltipSelecaoComDocumento(): string {
        return this.selection.selected.some(l => !!l.idNFeSaida)
            ? 'Há lançamentos gerados pela NF-e de saída na seleção: para alterar ou excluir, use "Excluir financeiro" na nota.'
            : 'Há lançamentos gerados pelo pedido de venda na seleção: altere ou exclua pelo menu Ações do pedido.';
    }

    /** Gerado por um documento (pedido de venda ou NF-e de saída): editar e excluir só pelo documento. */
    public isOrigemDocumento(lancamento: LancamentoFinanceiro): boolean {
        return !!lancamento.idVenda || !!lancamento.idNFeSaida;
    }

    public tooltipOrigemDocumento(lancamento: LancamentoFinanceiro): string {
        return lancamento.idNFeSaida
            ? 'Gerado pela NF-e de saída: para alterar ou excluir, use "Excluir financeiro" na nota.'
            : 'Gerado pelo pedido de venda: altere ou exclua pelo menu Ações do pedido.';
    }

    public get temSelecionados(): boolean {
        return this.selection.hasValue();
    }

    private stripHtml(html: string): string {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return (tmp.textContent || tmp.innerText || '').trim();
    }

    private mapSituacaoMovimentacao(idSituacaoMovimentacao: number, descricaoSituacaoMovimentacao?: string): 'conciliado' | 'naoConciliado' {
        if (idSituacaoMovimentacao === 1) {
            return 'conciliado';
        }

        if (idSituacaoMovimentacao === 2) {
            return 'naoConciliado';
        }

        const descricaoNormalizada = (descricaoSituacaoMovimentacao || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .toLowerCase();

        return descricaoNormalizada === 'conciliado' ? 'conciliado' : 'naoConciliado';
    }

    public get temFiltrosAvancados(): boolean {
        return this._filtrosAvancados.idsCategorias.length > 0
            || this._filtrosAvancados.idsTags.length > 0
            || this._filtrosAvancados.situacoes.length > 0;
    }

    public get qtdFiltrosAvancados(): number {
        return this._filtrosAvancados.idsCategorias.length
            + this._filtrosAvancados.idsTags.length
            + this._filtrosAvancados.situacoes.length;
    }

    private get isColunaClienteVisivel(): boolean {
        return this._colunasVisiveis.includes('cliente');
    }

    private carregarClientesDosLancamentos(): void {
        const idsPessoaPendentes = [...new Set(
            this._lancamentosBase
                .map(item => item.idPessoa)
                .filter((id): id is number => !!id && !this._clientesPorId.has(id))
        )];

        if (!idsPessoaPendentes.length) {
            this.aplicarClientesPorCache();
            return;
        }

        const request = new ListPessoaIdsRequest();
        request.listIdPessoa = idsPessoaPendentes;

        this._subscriptions.add(
            this.pessoaLookupService.getListPessoaIds(request).subscribe({
                next: response => {
                    if (response.isError) {
                        this.alertService.show('Erro', response.errorDescription || 'Erro ao carregar clientes.', AlertType.error);
                        this.aplicarClientesPorCache();
                        return;
                    }

                    (response.listaPessoa || []).forEach(pessoa => {
                        if (pessoa.idPessoa) {
                            this._clientesPorId.set(pessoa.idPessoa, pessoa.nome);
                        }
                    });

                    this.aplicarClientesPorCache();
                },
                error: () => {
                    this.alertService.show('Erro', 'Erro ao carregar clientes.', AlertType.error);
                    this.aplicarClientesPorCache();
                }
            })
        );
    }

    private aplicarClientesPorCache(): void {
        this._lancamentosBase = this._lancamentosBase.map(item => ({
            ...item,
            cliente: item.idPessoa ? this._clientesPorId.get(item.idPessoa) : undefined,
        }));

        this.aplicarFiltrosLocais();
    }

    private aplicarFiltrosLocais(): void {
        const situacoesSelecionadas = this._filtrosAvancados.situacoes || [];
        const listaFiltrada = !situacoesSelecionadas.length
            ? this._lancamentosBase
            : this._lancamentosBase.filter(item => situacoesSelecionadas.includes(item.situacao));

        this._dataSource.data = listaFiltrada;
    }

    private getFiltrosPrintRequest(): PrintMovimentacaoRequest {
        const raw = this.model?.getRawValue();
        const { dataInicio, dataFim } = this.buildDateRange();
        const request = new PrintMovimentacaoRequest();
        const idContaFinanceira = raw?.conta && raw.conta !== 'todas' ? Number(raw.conta) : undefined;
        const idsSituacaoMovimentacao = this.mapSituacoesFiltroParaIds();

        request.pesquisa = raw?.pesquisa || undefined;
        request.dataInicio = dataInicio;
        request.dataFim = dataFim;
        request.idContaFinanceira = idContaFinanceira;
        request.idsCategoriaFinanceira = this._filtrosAvancados.idsCategorias.length ? [...this._filtrosAvancados.idsCategorias] : undefined;
        request.idsTagsMovimentacao = this._filtrosAvancados.idsTags.length ? [...this._filtrosAvancados.idsTags] : undefined;
        request.idsSituacaoMovimentacao = idsSituacaoMovimentacao.length ? idsSituacaoMovimentacao : undefined;

        return request;
    }

    private exportarLancamentosCsv(itens: MovimentacaoGridItem[]): void {
        this.abrirModalExportacao(itens);
    }

    private baixarCsvLancamentos(itens: MovimentacaoGridItem[]): void {
        const cabecalho = [
            'Data',
            'Categoria',
            'Histórico',
            'Tipo',
            'Valor',
            'Tags',
            'Conta',
        ];

        const linhas = itens.map(item => [
            this.formatarDataCsv(item.dataMovimentacao),
            item.descricaoCategoria || '',
            this.stripHtml(item.descricao || ''),
            item.isEntrada ? 'Entrada' : 'Saída',
            this.formatarValorCsv(item.valor),
            item.tags.map(tag => tag.descricao).join(', '),
            item.descricaoContaFinanceira,
        ]);

        const csv = [cabecalho, ...linhas]
            .map(linha => linha.map(valor => this.escapeCsvValue(valor)).join(';'))
            .join('\r\n');

        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const dataAtual = new Date();
        const nomeArquivo = `lancamentos-financeiros-${dataAtual.getFullYear()}${String(dataAtual.getMonth() + 1).padStart(2, '0')}${String(dataAtual.getDate()).padStart(2, '0')}-${String(dataAtual.getHours()).padStart(2, '0')}${String(dataAtual.getMinutes()).padStart(2, '0')}.csv`;

        link.href = url;
        link.download = nomeArquivo;
        link.click();

        URL.revokeObjectURL(url);
        this.alertService.show('Sucesso', 'Lançamentos exportados com sucesso.', AlertType.success);
    }

    private abrirModalExportacao(itens: MovimentacaoGridItem[]): void {
        const colunas = [
            'Data',
            'Categoria',
            'Histórico',
            'Tipo',
            'Valor',
            'Tags',
            'Conta',
        ];

        const previewRows = this.mapearLinhasExportacao(itens).slice(0, 10);

        this.dialog.open(ModalExportarLancamentosComponent, {
            width: '960px',
            maxWidth: '95vw',
            data: {
                quantidadeRegistros: itens.length,
                colunas,
                previewRows
            }
        }).afterClosed().subscribe((confirmado: boolean | undefined) => {
            if (confirmado) {
                this.baixarCsvLancamentos(itens);
            }
        });
    }

    private mapearLinhasExportacao(itens: MovimentacaoGridItem[]): ExportacaoLancamentoPreviewRow[] {
        return itens.map(item => ({
            data: this.formatarDataCsv(item.dataMovimentacao),
            categoria: item.descricaoCategoria || '',
            historico: this.stripHtml(item.descricao || ''),
            tipo: item.isEntrada ? 'Entrada' : 'Saída',
            valor: this.formatarValorCsv(item.valor),
            tags: item.tags.map(tag => tag.descricao).join(', '),
            conta: item.descricaoContaFinanceira,
        }));
    }

    private formatarDataCsv(data: string): string {
        return new Intl.DateTimeFormat('pt-BR').format(new Date(data));
    }

    private formatarValorCsv(valor: number): string {
        return new Intl.NumberFormat('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(valor);
    }

    private escapeCsvValue(valor: string): string {
        const texto = String(valor ?? '');
        const textoEscapado = texto.replace(/"/g, '""');
        return `"${textoEscapado}"`;
    }

    private mapSituacoesFiltroParaIds(): number[] {
        return (this._filtrosAvancados.situacoes || [])
            .reduce<number[]>((ids, situacao) => {
                if (situacao === 'conciliado') {
                    ids.push(1);
                } else if (situacao === 'naoConciliado') {
                    ids.push(2);
                }

                return ids;
            }, []);
    }
}
